import { SettingUrls } from 'constants/SettingsUrls';
import useSendGTMAction from 'features/google-tag-manager/hooks/useSendGTMAction';
import { UpsellKey } from 'features/google-tag-manager/enum/upsell-key';
import { UpsellClickedGtmAction } from 'features/google-tag-manager/model/actions/upsell_clicked.gtm-action';
import { useAppStatusSWR } from 'features/settings/components/ppoints/dashboard/app-status/hooks/useAppStatusSWR';
import useProviderLimitExceeded from 'hooks/plan/useProviderLimitExceeded';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AppStatus } from '../../../../pb/settings/v1/app_status_pb';
import { ProviderLimitExceededModal } from './ProviderLimitExceededModal';

/**
 * Tells the merchant the app is inactive because more carriers are active than the current plan allows.
 * Self-guards on that check and on the app really being inactive, so callers can render it unconditionally.
 */
export const ProviderLimitExceededBanner: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const sendGTMAction = useSendGTMAction();
    const modalId = useId();
    const { isLimitExceeded, limit, planName } = useProviderLimitExceeded();
    const { data: appStatus, isLoading: isAppStatusLoading } = useAppStatusSWR();

    if (!isLimitExceeded || isAppStatusLoading || appStatus?.appStatus === AppStatus.ACTIVE) {
        return null;
    }

    const handleUpgrade = () => {
        sendGTMAction(new UpsellClickedGtmAction({ name: UpsellKey.PROVIDER_LIMIT_EXCEEDED }));
        navigate(SettingUrls.billing);
    };

    return (
        <>
            <s-banner
                tone="warning"
                heading={t('PROVIDER_LIMIT_EXCEEDED_BANNER.HEADING', { count: limit, plan: planName })}
            >
                <div>{t('PROVIDER_LIMIT_EXCEEDED_BANNER.DESCRIPTION', { count: limit })}</div>
                <div className="flex gap-2 mt-2">
                    <s-button variant="primary" commandFor={modalId} command="--show">
                        {t('PROVIDER_LIMIT_EXCEEDED_BANNER.CHOOSE_CARRIER', { count: limit })}
                    </s-button>
                    <s-button onClick={handleUpgrade}>{t('PROVIDER_LIMIT_EXCEEDED_BANNER.UPGRADE_PLAN')}</s-button>
                </div>
            </s-banner>
            <ProviderLimitExceededModal id={modalId} limit={limit} planName={planName} />
        </>
    );
};
